import { LogLevel } from '@azure/msal-browser';
import type { Configuration } from '@azure/msal-browser';

/**
 * MSAL configuration, from build-time environment.
 *
 * The values mirror `infra/cdk.json`'s `ira` context block one for one — `tenantId`,
 * `spaClientId`, `apiClientId`, `tokenVersion`. They are injected at build time by Vite, so a
 * dashboard bundle is tied to one tenant and one app registration; there is no runtime
 * tenant switch and no way for a deployed bundle to be pointed at another directory.
 */

function required(name: string, value: string | undefined): string {
  const trimmed = (value ?? '').trim();
  if (!trimmed) {
    // Thrown at module load, so a misconfigured deploy fails visibly on the first page load
    // rather than producing a login loop with no explanation.
    throw new Error(
      `${name} is not set. The dashboard cannot authenticate without it — see web/.env.example.`,
    );
  }
  return trimmed;
}

export const TENANT_ID = required('VITE_TENANT_ID', import.meta.env.VITE_TENANT_ID);
export const SPA_CLIENT_ID = required('VITE_SPA_CLIENT_ID', import.meta.env.VITE_SPA_CLIENT_ID);
export const API_CLIENT_ID = required('VITE_API_CLIENT_ID', import.meta.env.VITE_API_CLIENT_ID);

export const API_BASE_URL = (import.meta.env.VITE_API_BASE_URL ?? '').trim().replace(/\/$/, '');

/**
 * The scope the access token is requested for.
 *
 * `api://<apiClientId>/access_as_user` — the API's own scope, not Graph. Requesting a Graph
 * scope here yields a token the API's JWT authorizer rejects on audience, which surfaces as a
 * 401 that names nothing useful. `docs/decisions.md` D10 has the detail.
 */
export const API_SCOPE = `api://${API_CLIENT_ID}/access_as_user`;

export const msalConfig: Configuration = {
  auth: {
    clientId: SPA_CLIENT_ID,
    authority: `https://login.microsoftonline.com/${TENANT_ID}`,
    // Derived from the current origin rather than hardcoded, so one bundle works from
    // https://ira.cscf.torrens.edu.au and from localhost during development. Both origins
    // have to be registered on the SPA app registration as bare origins with no path —
    // an unregistered one fails with AADSTS50011.
    redirectUri: window.location.origin,
    postLogoutRedirectUri: window.location.origin,
    // Single tenant: the authority already pins it, and this refuses a token from any other
    // directory before it reaches the API's own `tid` check.
    navigateToLoginRequestUrl: true,
  },
  cache: {
    // sessionStorage rather than localStorage: this is a console that can revoke sessions and
    // disable accounts, and a token surviving a closed browser on a shared or unattended
    // machine is a larger risk than the cost of signing in again.
    cacheLocation: 'sessionStorage',
    storeAuthStateInCookie: false,
  },
  system: {
    loggerOptions: {
      logLevel: import.meta.env.DEV ? LogLevel.Warning : LogLevel.Error,
      piiLoggingEnabled: false,
      loggerCallback: (level, message, containsPii) => {
        if (containsPii) return;
        if (level === LogLevel.Error) console.error('[msal]', message);
        else if (level === LogLevel.Warning) console.warn('[msal]', message);
      },
    },
  },
};

/** Interactive login only needs the API scope; the ID token comes with it. */
export const loginRequest = {
  scopes: [API_SCOPE],
};
